import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/shared/i18n";
import * as React from "react";

export interface LoadingIndicatorProps {
  /** Fill the viewport — used as the Suspense fallback for lazy routes. */
  fullPageHeight?: boolean;
  size?: "sm" | "md" | "lg";
  /** Show the "Loading" label. Off by default; the spinner usually suffices. */
  label?: boolean;
  className?: string;
}

const LoadingIndicator = ({
  fullPageHeight = false,
  size = "md",
  label = false,
  className,
}: LoadingIndicatorProps) => {
  const { t } = useTranslation();
  // Only announce a wait that a person would actually notice — otherwise a
  // fast route change makes a screen reader chatter for no reason.
  const [announce, setAnnounce] = React.useState(false);
  React.useEffect(() => {
    const timer = setTimeout(() => setAnnounce(true), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-5",
        fullPageHeight ? "min-h-[60vh] py-24" : "py-14",
        className
      )}
    >
      <Spinner size={size} className="text-kf-taupe" />
      {label || announce ? (
        <span
          className={cn(
            "font-heading text-eyebrow font-medium uppercase tracking-wide2 text-kf-muted",
            !label && "sr-only"
          )}
        >
          {t("common.loading")}
        </span>
      ) : null}
    </div>
  );
};

export { LoadingIndicator };
